import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import './DailyReceipt.css';

const MODES = ['Cash', 'Card', 'UPI', 'Bank Transfer', 'Cheque'];
const EMPTY = {
  receipt_date: today(),
  items: [{ mode: 'Cash', bank_id: '', description: '', amount: '', file: null }],
};

export default function DailyReceipt({ entryDate }) {
  const { t } = useTranslation();
  const { user, canAccess, can } = useAuth();
  const baseDate = entryDate || today();
  // In Daily Entry the list is retrieved for the selected business date.
  const { data: receipts, loading, error, reload } = useApi(() => api.dailyReceipts.list(entryDate), [entryDate]);
  const { data: bankData, loading: banksLoading } = useApi(() => api.banks.list(), [], { banks: [] });
  const banks = bankData?.banks || [];

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY, receipt_date: baseDate });
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirm, setConfirm] = useState(null);

  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };
  const isToday = (d) => d === today();

  const canUpdate = can('daily_receipts', 'update');

  // Auto-select bank for the first item when form opens or banks load
  React.useEffect(() => {
    if (showForm && banks.length > 0 && form.items.length === 1 && !form.items[0].bank_id) {
      const filtered = getFilteredBanks(form.items[0].mode);
      const def = filtered.find(b => Number(b.is_default) === 1) || filtered[0];
      if (def) {
        const it = [...form.items];
        it[0].bank_id = def.id;
        setForm(prev => ({ ...prev, items: it }));
      }
    }
  }, [showForm, banks]);

  // Helper to filter banks per item
  const getFilteredBanks = (mode) => {
    const cash = banks.filter(b => b.account_type === 'Cash' || b.name.toLowerCase().includes('cash'));
    const nonCash = banks.filter(b => b.account_type !== 'Cash' && !b.name.toLowerCase().includes('cash'));
    if (mode === 'Cash') return cash.length > 0 ? cash : banks;
    return nonCash.length > 0 ? nonCash : banks;
  };

  const addItem = () => {
    const last = form.items[form.items.length - 1];
    setForm({
      ...form,
      items: [...form.items, { mode: last?.mode || 'Cash', bank_id: last?.bank_id || '', description: '', amount: '', file: null }]
    });
  };

  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const updateItem = (idx, f, v) => {
    const it = [...form.items];
    it[idx][f] = v;
    if (f === 'mode') {
      const filtered = getFilteredBanks(v);
      const stillValid = filtered.find(b => b.id === Number(it[idx].bank_id));
      if (!stillValid) {
        const defaultBank = filtered.find(b => Number(b.is_default) === 1) || filtered[0];
        it[idx].bank_id = defaultBank ? defaultBank.id : '';
      }
    }
    setForm({ ...form, items: it });
  };

  const handleSave = async () => {
    const validItems = form.items.filter(it => it.description && Number(it.amount) > 0 && it.bank_id);
    if (validItems.length === 0) { notify('❌ ' + t('dailyReceipt.errors.fillOneItem')); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('receipt_date', form.receipt_date);

      // Map items and attach files individually
      const itemsPayload = validItems.map((it, idx) => {
        if (it.file) {
          fd.append(`file_${idx}`, it.file);
        }
        return {
          mode: it.mode,
          bank_id: it.bank_id,
          description: it.description,
          amount: it.amount,
          has_file: !!it.file,
          file_index: idx
        };
      });

      fd.append('items', JSON.stringify(itemsPayload));

      await api.dailyReceipts.create(fd);
      notify('✅ ' + t('dailyReceipt.toasts.saved')); setShowForm(false); setForm({ ...EMPTY, receipt_date: baseDate }); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleEditSave = async () => {
    if (!editRow.bank_id) { notify('❌ ' + t('dailyReceipt.errors.selectBank')); return; }
    setSaving(true);
    try {
      await api.dailyReceipts.update(editRow.id, {
        description: editRow.description,
        mode: editRow.mode,
        bank_id: parseInt(editRow.bank_id),
        amount: parseFloat(editRow.amount)
      });
      notify('✅ ' + t('common.updatedSuccessfully')); setEditRow(null); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try { await api.dailyReceipts.remove(confirm.row.id); notify('✅ ' + t('common.deletedSuccessfully')); reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  const handleViewAttachment = async (id) => {
    try {
      const blob = await api.dailyReceipts.downloadAttachment(id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      notify('❌ ' + t('common.error') + ': ' + e.message);
    }
  };

  const total = (receipts || []).reduce((s, r) => s + Number(r.amount || 0), 0);
  
  if (!canAccess('daily_receipts')) {
    return (
      <div className="daily-receipt-page">
        <ErrBanner msg={t('dailyReceipt.accessDenied')} />
      </div>
    );
  }

  return (
    <div className="daily-receipt-page">
      <div className="page-header">
        <h2 className="page-title">{t('dailyReceipt.title')}</h2>
        {can('daily_receipts', 'create') && (
          <button className="btn btn-primary btn-sm" onClick={() => {
            setShowForm(!showForm);
            setEditRow(null);
            setForm({ ...EMPTY, receipt_date: baseDate });
          }}>
            {showForm ? '✕ ' + t('common.cancel') : '＋ ' + t('dailyReceipt.addReceipt')}
          </button>
        )}
      </div>
      {msg && <div style={{ padding: '6px 12px', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 6, marginBottom: 8, fontSize: 13 }}>{msg}</div>}

      {showForm && (
        <div className="section-card">
          <div className="section-header">{t('dailyReceipt.newReceiptEntry')}</div>
          <div style={{ padding: '0 16px 16px' }}>
            <div className="form-row" style={{ marginBottom: 16 }}>
              <div className="form-group" style={{ maxWidth: 200 }}><label>{t('common.date')}</label>
                <input type="date" className="form-control" value={form.receipt_date}
                  onChange={e => setForm({ ...form, receipt_date: e.target.value })} />
              </div>
            </div>

            <table className="data-table" style={{ marginBottom: 16 }}>
              <thead>
                <tr>
                  <th style={{ width: '12%' }}>{t('dailyReceipt.mode')}</th>
                  <th>{t('common.description')}</th>
                  <th style={{ width: '20%' }}>{t('dailyReceipt.accountBank')}</th>
                  <th style={{ width: '12%' }}>{t('dailyReceipt.amount')}</th>
                  <th style={{ width: '15%' }}>{t('dailyReceipt.attachment')}</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => (
                  <tr key={idx}>
                    <td>
                      <select className="form-control" value={it.mode} onChange={e => updateItem(idx, 'mode', e.target.value)}>
                        {MODES.map(m => <option key={m}>{m}</option>)}
                      </select>
                    </td>
                    <td><input className="form-control" value={it.description} placeholder={t('dailyReceipt.descriptionPlaceholder')} onChange={e => updateItem(idx, 'description', e.target.value)} /></td>
                    <td>
                      <select className="form-control" value={it.bank_id} onChange={e => updateItem(idx, 'bank_id', e.target.value)}>
                        <option value="">{banksLoading ? '...' : t('dailyReceipt.selectOption')}</option>
                        {getFilteredBanks(it.mode).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                    </td>
                    <td><input type="number" className="form-control" value={it.amount} onChange={e => updateItem(idx, 'amount', e.target.value)} /></td>
                    <td>
                      <div className="custom-file-upload-small">
                        {it.file ? `📄 ${it.file.name.substring(0, 10)}...` : '📁 ' + t('dailyReceipt.file')}
                        <input type="file" onChange={e => updateItem(idx, 'file', e.target.files[0])} />
                      </div>
                    </td>
                    <td>
                      {form.items.length > 1 && (
                        <button className="btn btn-sm" style={{ color: 'var(--danger)' }} onClick={() => removeItem(idx)}>🗑️</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button className="btn btn-secondary btn-sm" onClick={addItem}>＋ {t('dailyReceipt.addAnotherRow')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('common.saving') : '💾 ' + t('dailyReceipt.saveReceipt')}</button>
            </div>
          </div>
        </div>
      )}

      {editRow && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ {t('dailyReceipt.editReceipt')} — {editRow.receipt_no} — {fmtDate(editRow.receipt_date)}</div>
          <div className="form-row">
            <div className="form-group"><label>{t('dailyReceipt.mode')}</label>
              <select className="form-control" value={editRow.mode} onChange={e => setEditRow({ ...editRow, mode: e.target.value })}>
                {MODES.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="form-group"><label>{t('dailyReceipt.accountBank')}</label>
              <select className="form-control" value={editRow.bank_id} onChange={e => setEditRow({ ...editRow, bank_id: e.target.value })}>
                <option value="">{banksLoading ? '...' : t('dailyReceipt.selectOption')}</option>
                {getFilteredBanks(editRow.mode).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>{t('common.description')}</label>
              <input className="form-control" value={editRow.description} onChange={e => setEditRow({ ...editRow, description: e.target.value })} />
            </div>
            <div className="form-group"><label>{t('dailyReceipt.amount')}</label>
              <input type="number" className="form-control" value={editRow.amount} onChange={e => setEditRow({ ...editRow, amount: e.target.value })} />
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleEditSave} disabled={saving}>{saving ? t('common.saving') : '💾 ' + t('common.update')}</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ {t('common.cancel')}</button>
          </div>
        </div>
      )}

      {loading && <Spinner />}
      {error && <ErrBanner msg={error} />}
      {!loading && (
        <div className="section-card">
          <div className="section-header">{t('dailyReceipt.listHeader')} — {t('common.total')}: ₹{fmtNum(total)}</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th>#</th><th style={{ textAlign: 'left' }}>{t('common.date')}</th><th style={{ textAlign: 'left' }}>{t('dailyReceipt.receiptNo')}</th>
                <th style={{ textAlign: 'left' }}>{t('dailyReceipt.mode')}</th><th style={{ textAlign: 'left' }}>{t('dailyReceipt.account')}</th>
                <th style={{ textAlign: 'left' }}>{t('common.description')}</th>
                <th style={{ textAlign: 'right' }}>{t('dailyReceipt.amount')}</th><th style={{ textAlign: 'center' }}>{t('common.actions')}</th>
              </tr></thead>
              <tbody>
                {(receipts || []).map((r, i) => (
                  <tr key={r.id}>
                    <td>{i + 1}</td><td>{fmtDate(r.receipt_date)}</td>
                    <td style={{ fontWeight: 700, color: 'var(--primary)', fontFamily: 'monospace' }}>
                      {r.receipt_no}
                      {r.file_path && (
                        <button onClick={() => handleViewAttachment(r.id)} title={t('dailyReceipt.viewAttachment')} style={{ marginLeft: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>📎</button>
                      )}
                    </td>
                    <td><span className="badge badge-info">{r.mode}</span></td>
                    <td style={{ fontWeight: 600 }}>{r.bank_name || <span style={{ color: '#ccc', fontStyle: 'italic' }}>{t('dailyReceipt.unallocated')}</span>}</td>
                    <td>{r.description}</td>
                    <td className="right-align">₹{fmtNum(r.amount)}</td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {can('daily_receipts', 'update') && (isToday(r.receipt_date) || canUpdate) && (<button title={t('common.edit')} style={btnEdit} onClick={() => setEditRow({ ...r })}>✏️</button>
                      )}
                      {can('daily_receipts', 'delete') && (
                        <button title={t('common.delete')} style={btnDel} onClick={() => setConfirm({ row: r })}>🗑️</button>
                      )}
                    </td>
                  </tr>
                ))}
                {(receipts || []).length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>{t('dailyReceipt.noReceipts')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title={t('dailyReceipt.deleteReceipt')}
        message={t('dailyReceipt.confirmDeleteMsg', { receiptNo: confirm?.row?.receipt_no })}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

const btnEdit = { background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', marginRight: 4, fontSize: 13 };
const btnDel = { background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 13 };
